import React, { useState } from 'react'
import moment from 'moment'
import { scheduleAppointment } from '../utils/appointmentAPI'

// Individual appointment time slot button
const ApptSlot = ({ option, highlight }) => {
    const highlightStyle = highlight ? { boxShadow: '0 0 0 4px rgb(255, 188, 44)' } : {}
    return (
        <div
            className="border border-primary rounded-3 p-3 m-2 bg-light text-dark text-center"
            style={{ minWidth: '120px', cursor: 'pointer', ...highlightStyle }}
        >
            <div className="fw-bold fs-6">{moment(option.start.slice(0, -5)).format('LT')}</div>
            <div className="small text-muted">{option.duration}</div>
        </div>
    )
}

// Appointment windows for one pickup/delivery type.
// Replaces OpenApptWindowsView + ApptOptionTwo with cleaner state management.
const AppointmentWindows = ({ type, consolidatedWindows, existingAppts, shipment, reload }) => {
    const [crudInProgress, setCrudInProgress] = useState(false)
    const [scheduleError, setScheduleError] = useState(false)

    const cftoken = new URLSearchParams(window.location.search).get('cftoken') ?? null

    const windows = consolidatedWindows || []

    if (!windows.length) {
        return (
            <div className="d-flex justify-content-center mt-3">
                <div className="alert alert-secondary border border-dark rounded" role="alert">
                    No appointments available at the specified date(s).
                </div>
            </div>
        )
    }

    const handleSlotClick = async (option, adminDate) => {
        if (crudInProgress) return
        setScheduleError(false)
        setCrudInProgress(true)

        const success = await scheduleAppointment({ type, option, existingAppts, shipment, adminDate, cftoken })

        if (success) {
            // Brief pause so any flash of success is visible, then reload
            setTimeout(async () => {
                await reload()
                setCrudInProgress(false)
            }, 1500)
        } else {
            setScheduleError(true)
            setCrudInProgress(false)
        }
    }

    // Use a stable random key prefix so multiple accordion instances on the same
    // page (consolidations) don't share IDs
    const keyPrefix = `qs-${Math.floor(Math.random() * 1000000)}`

    return (
        <div className="m-2 m-md-3">
            {scheduleError && (
                <div className="alert alert-danger mb-3" role="alert">
                    That appointment window is no longer available. Please select another time.
                </div>
            )}

            {windows.map((apptDate, index) => {
                const collapseId = `${keyPrefix}-${index}`
                const isOnlyDate = windows.length === 1
                const availableCount = apptDate.docks.reduce((sum, dock) => sum + dock.options.length, 0)
                const hasSupplierRules = apptDate.docks.some(d => d.options.some(o => o.supplierIncludeRule))

                return (
                    <div key={collapseId} className="accordion accordion-flush mb-2">
                        <div className="accordion-item border">
                            <h2 className="accordion-header">
                                <button
                                    className={`accordion-button ${isOnlyDate ? '' : 'collapsed'}`}
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#${collapseId}`}
                                    aria-expanded={isOnlyDate ? 'true' : 'false'}
                                    aria-controls={collapseId}
                                >
                                    <div className="d-flex justify-content-between w-100 me-2 flex-wrap gap-1">
                                        <strong>
                                            {moment(apptDate.date.replace(/-/g, '/').replace(/T.+/, '')).format('dddd MMMM DD, YYYY')}
                                        </strong>
                                        <span className="text-muted fw-light small">
                                            ({availableCount} available)
                                        </span>
                                    </div>
                                </button>
                            </h2>

                            <div
                                id={collapseId}
                                className={`accordion-collapse collapse ${isOnlyDate ? 'show' : ''}`}
                            >
                                <div className="accordion-body">
                                    {crudInProgress ? (
                                        <div className="d-flex flex-column align-items-center py-4">
                                            <img
                                                src="https://scheduler.shippersedge.com/loading.gif"
                                                alt="Scheduling..."
                                                height="150"
                                            />
                                            <p className="mt-2 fw-bold">Scheduling your appointment...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {hasSupplierRules && (
                                                <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="alert">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-exclamation-circle-fill flex-shrink-0 mt-1" viewBox="0 0 16 16">
                                                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4m.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2" />
                                                    </svg>
                                                    <span>The highlighted appointments are prioritized for your shipment. Please consider selecting these first.</span>
                                                </div>
                                            )}

                                            {apptDate.docks.map((dock, dockIndex) => {
                                                const sorted = [...dock.options].sort(
                                                    (a, b) => new Date(a.start) - new Date(b.start)
                                                )
                                                return (
                                                    <div key={dockIndex} className="d-flex flex-wrap">
                                                        {sorted.map((option, optIndex) => (
                                                            <div
                                                                key={optIndex}
                                                                onClick={() => handleSlotClick(option, apptDate.date)}
                                                            >
                                                                <ApptSlot
                                                                    option={option}
                                                                    highlight={!!option.supplierIncludeRule}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )
                                            })}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default AppointmentWindows
